// The arithmetic behind the Committee History summary. Pure functions over the
// term, member and secretary-handover records, so the screen only lays the
// answers out.
//
// Business status lives in termStatus / memberStatus / handoverStatus; the
// plain `status` column is the lifecycle flag (ACTIVE / VOID) and is not read.

import { countBy, dateOf, daysUntil, isoOf, percent } from '../../utils/summaryStats';
import type { NamedCount } from '../../utils/summaryStats';

export interface TermLike {
  recordId?: string;
  name?: string;
  startDate?: string;
  endDate?: string;
  /** Active / Completed. */
  termStatus?: string;
}

export interface CommitteeMemberLike {
  termId?: string;
  name?: string;
  role?: string;
  fromDate?: string;
  toDate?: string;
  /** Active / Completed / Resigned / Deceased. */
  memberStatus?: string;
}

export interface SecretaryHandoverLike {
  recordId?: string;
  date?: string;
  outgoing?: string;
  incoming?: string;
  /** Pending / Completed. */
  handoverStatus?: string;
  adminKeys?: unknown;
  finRecords?: unknown;
  minuteBooks?: unknown;
  legalDocs?: unknown;
}

/** The secretary's handover checklist, in the order the form shows it. */
export const HANDOVER_ITEMS = [
  { key: 'adminKeys', label: 'Office Keys & Safe Access' },
  { key: 'minuteBooks', label: 'Committee Minute Books' },
  { key: 'finRecords', label: 'Financial Records & Cheque Books' },
  { key: 'legalDocs', label: 'Property & Legal Documents' }
] as const;

export interface TermRow {
  recordId: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  members: number;
}

export interface ServiceRecord {
  name: string;
  terms: number;
  roles: string;
}

export interface HandoverRow {
  key: string;
  date: string;
  outgoing: string;
  incoming: string;
  status: string;
  done: number;
  pct: number;
  missing: string[];
}

/** One line of the watch-list: what needs attention, and by when. */
export interface CommitteeAlert {
  key: string;
  kind: string;
  subject: string;
  date: string;
  /** Days until `date`; negative when already past; null when undated. */
  days: number | null;
}

export interface CommitteeSummary {
  current: TermRow | null;
  daysLeft: number | null;
  /** Share of the current term already served, 0–100. */
  elapsedPct: number | null;
  serving: CommitteeMemberLike[];
  officeBearers: CommitteeMemberLike[];
  byRole: NamedCount[];
  byStatus: NamedCount[];
  terms: TermRow[];
  people: number;
  mostServed: ServiceRecord[];
  handovers: HandoverRow[];
  checklistPct: number;
  items: { key: string; label: string; done: number }[];
  pending: HandoverRow[];
  alerts: CommitteeAlert[];
}

/** How far ahead a term's end starts to show on the watch-list. */
export const TERM_WARNING_DAYS = 90;

const norm = (v: unknown) => String(v ?? '').trim();
const isTicked = (v: unknown): boolean => v === true || v === 'true';

/** A term's status; a blank one is read from whether today falls inside it. */
export const termStatusOf = (x: TermLike, now: Date = new Date()): string => {
  const s = norm(x.termStatus);
  if (s) return s;
  const today = isoOf(now);
  const end = dateOf(x.endDate);
  return (dateOf(x.startDate) || '') <= today && (!end || end >= today) ? 'Active' : 'Completed';
};

/** A member's status; blank reads as Active, as the form defaults it. */
export const memberStatusOf = (m: CommitteeMemberLike): string => norm(m.memberStatus) || 'Active';

/** An ordinary seat rather than an office. */
export const isPlainMember = (role: unknown): boolean => /^(committee\s+)?members?$/i.test(norm(role));

// Offices listed chair first, then secretary, treasurer and the rest.
const OFFICE_ORDER = [/chair|president/i, /vice/i, /^secretary$/i, /secretary/i, /treasurer/i, /warden/i];
const officeRank = (role: unknown) => {
  const i = OFFICE_ORDER.findIndex((re) => re.test(norm(role)));
  return i === -1 ? OFFICE_ORDER.length : i;
};

export const committeeSummary = (
  terms: TermLike[], members: CommitteeMemberLike[], handovers: SecretaryHandoverLike[], now: Date = new Date()
): CommitteeSummary => {
  const today = isoOf(now);
  const termRows: TermRow[] = terms
    .map((x) => ({
      recordId: norm(x.recordId),
      name: norm(x.name) || norm(x.recordId) || '-',
      startDate: dateOf(x.startDate) || '',
      endDate: dateOf(x.endDate) || '',
      status: termStatusOf(x, now),
      members: members.filter((m) => norm(m.termId) && norm(m.termId) === norm(x.recordId)).length
    }))
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  // Only one term should be Active; if several are, the latest to start wins.
  const current = termRows.find((r) => r.status === 'Active') || null;
  const daysLeft = current ? daysUntil(current.endDate, now) : null;
  let elapsedPct: number | null = null;
  if (current && current.startDate && current.endDate) {
    const total = daysUntil(current.endDate, now)! - daysUntil(current.startDate, now)!;
    const done = -daysUntil(current.startDate, now)!;
    elapsedPct = Math.max(0, Math.min(100, percent(done, total)));
  }

  const serving = current
    ? members.filter((m) => norm(m.termId) === current.recordId && memberStatusOf(m) === 'Active')
    : [];
  const officeBearers = serving
    .filter((m) => !isPlainMember(m.role))
    .sort((a, b) => officeRank(a.role) - officeRank(b.role) || norm(a.role).localeCompare(norm(b.role)));

  // People who served most: distinct terms per name (case-insensitive).
  const service = new Map<string, { name: string; terms: Set<string>; roles: Set<string> }>();
  members.forEach((m) => {
    const name = norm(m.name);
    if (!name) return;
    const k = name.toLowerCase();
    const cur = service.get(k) || { name, terms: new Set<string>(), roles: new Set<string>() };
    cur.terms.add(norm(m.termId) || `${m.fromDate}`);
    if (norm(m.role)) cur.roles.add(norm(m.role));
    service.set(k, cur);
  });
  const mostServed = Array.from(service.values())
    .map((x) => ({ name: x.name, terms: x.terms.size, roles: Array.from(x.roles).join(', ') }))
    .sort((a, b) => b.terms - a.terms || a.name.localeCompare(b.name));

  const rows: HandoverRow[] = handovers
    .map((h, i) => {
      const missing = HANDOVER_ITEMS.filter((it) => !isTicked(h[it.key])).map((it) => it.label);
      const done = HANDOVER_ITEMS.length - missing.length;
      return {
        key: h.recordId || `${h.date}|${i}`,
        date: dateOf(h.date) || '',
        outgoing: norm(h.outgoing) || '-',
        incoming: norm(h.incoming) || '-',
        status: norm(h.handoverStatus) || 'Pending',
        done,
        pct: percent(done, HANDOVER_ITEMS.length),
        missing
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));

  const alerts: CommitteeAlert[] = [];
  if (current && daysLeft !== null && daysLeft <= TERM_WARNING_DAYS) {
    alerts.push({
      key: `term|${current.recordId}`, kind: daysLeft < 0 ? 'Term overran' : 'Term ending',
      subject: current.name, date: current.endDate, days: daysLeft
    });
  }
  if (!current && terms.length) {
    alerts.push({ key: 'term|none', kind: 'No active term', subject: 'No term is marked Active', date: '', days: null });
  }
  // Still marked Active after their own end date.
  members
    .filter((m) => memberStatusOf(m) === 'Active' && (dateOf(m.toDate) || '9999') < today)
    .forEach((m, i) => alerts.push({
      key: `member|${m.name}|${i}`, kind: 'Past end date, still Active',
      subject: `${norm(m.name) || '-'} (${norm(m.role) || 'Member'})`,
      date: dateOf(m.toDate) || '', days: daysUntil(m.toDate, now)
    }));
  rows
    .filter((r) => r.status !== 'Completed' || r.missing.length)
    .forEach((r) => alerts.push({
      key: `handover|${r.key}`,
      kind: r.status !== 'Completed' ? 'Handover pending' : 'Handover incomplete',
      subject: `${r.outgoing} → ${r.incoming} (${r.pct}%)`,
      date: r.date, days: daysUntil(r.date, now)
    }));

  const ticked = rows.reduce((n, r) => n + r.done, 0);

  return {
    current,
    daysLeft,
    elapsedPct,
    serving,
    officeBearers,
    byRole: countBy(serving, (m) => m.role),
    byStatus: countBy(members, memberStatusOf),
    terms: termRows,
    people: service.size,
    mostServed,
    handovers: rows,
    checklistPct: percent(ticked, rows.length * HANDOVER_ITEMS.length),
    items: HANDOVER_ITEMS.map((it) => ({
      key: it.key, label: it.label, done: handovers.filter((h) => isTicked(h[it.key])).length
    })),
    pending: rows.filter((r) => r.status !== 'Completed'),
    alerts
  };
};
